/**
 * Detect if the current browser is a WebView (in-app browser)
 *
 * WebViews are embedded browsers within apps like Facebook, Instagram, Threads,
 * LINE, WeChat, etc. Google blocks OAuth in WebViews for security reasons.
 *
 * @returns true if running in a WebView environment
 */
export function isWebView(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent || '';

  // Common WebView User-Agent patterns
  const webviewPatterns = [
    // Facebook family (Facebook, Instagram, Threads, Messenger)
    /FB_IAB/i, // Facebook In-App Browser
    /FBAN/i, // Facebook App
    /FBAV/i, // Facebook App Version
    /Instagram/i, // Instagram
    /Threads/i, // Threads
    /Barcelona/i, // Threads (internal codename)
    /IABMV/i, // In-App Browser Mobile Version (Meta apps)

    // Messaging apps
    /Line/i, // LINE
    /KAKAOTALK/i, // KakaoTalk
    /WeChat/i, // WeChat
    /MicroMessenger/i, // WeChat (alternative)
    /Telegram/i, // Telegram

    // Professional/Social networks
    /LinkedIn/i, // LinkedIn
    /Snapchat/i, // Snapchat
    /Pinterest/i, // Pinterest
    /TikTok/i, // TikTok

    // Twitter/X
    /Twitter/i,

    // Generic WebView indicators
    /wv\)/i, // Android WebView marker
    /WebView/i, // Generic WebView

    // iOS WebView indicators (not Safari)
    // Note: WKWebView often appears as Safari, so we check for app-specific markers
  ];

  // Check against all patterns
  const isWebViewUA = webviewPatterns.some((pattern) => pattern.test(ua));

  // Additional check for iOS: Safari-based WebView detection
  // Real Safari has "Safari" in UA but no app-specific markers
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome/i.test(ua);
  const hasAppMarker =
    /FBAN|FBAV|Instagram|Threads|Barcelona|IABMV|LinkedIn|Line|Twitter|TikTok|Snapchat|Pinterest/i.test(
      ua
    );

  // iOS WebView: Safari-like UA but with app markers
  const isIOSWebView = isIOS && isSafari && hasAppMarker;

  return isWebViewUA || isIOSWebView;
}

/**
 * Get the detected WebView app name (if detectable)
 *
 * @returns App name or null if not in WebView or unknown
 */
export function getWebViewAppName(): string | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const ua = navigator.userAgent || '';

  // Map of patterns to app names
  const appPatterns: [RegExp, string][] = [
    [/Threads|Barcelona/i, 'Threads'],
    [/Instagram/i, 'Instagram'],
    [/FBAN|FBAV|FB_IAB|IABMV/i, 'Facebook'],
    [/LinkedIn/i, 'LinkedIn'],
    [/Line\//i, 'LINE'],
    [/KAKAOTALK/i, 'KakaoTalk'],
    [/MicroMessenger|WeChat/i, 'WeChat'],
    [/Twitter/i, 'Twitter'],
    [/Telegram/i, 'Telegram'],
    [/Snapchat/i, 'Snapchat'],
    [/Pinterest/i, 'Pinterest'],
    [/TikTok/i, 'TikTok'],
  ];

  for (const [pattern, name] of appPatterns) {
    if (pattern.test(ua)) {
      return name;
    }
  }

  // Generic WebView detection
  if (/wv\)|WebView/i.test(ua)) {
    return 'App';
  }

  return null;
}
